import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Loader2, Phone, Lock } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/site/Logo";
import { LangSwitcher } from "@/components/site/LangSwitcher";
import { STEPS, stepValid, isKeyService } from "@/components/wizard/Steps";
import { Summary } from "@/components/wizard/Summary";
import { api, errMsg, PHONE_DISPLAY, PHONE_TEL, WHATSAPP_NUMBER } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { WIZ, W_SERVICES } from "@/lib/locales/wizard";

const EMPTY = { service: "", vehicle_type: "", make: "", model: "", year: "", vin: "", key_status: "", place: "", location: "", urgency: "", schedule_date: "", schedule_time: "", name: "", phone: "", email: "", preferred_contact: "", message: "", key_photos: [] };

export default function QuoteWizard() {
  const { lang } = useLang();
  const w = WIZ[lang] || WIZ.en;
  const [params] = useSearchParams();
  const initialService = W_SERVICES.includes(params.get("service")) ? params.get("service") : "";
  const [f, setF] = useState({ ...EMPTY, service: initialService });
  const [step, setStep] = useState(initialService ? 1 : 0);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step, done]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const total = STEPS.length;
  const next = () => setStep((s) => Math.min(s + 1, total - 1));
  const autoNext = () => setTimeout(next, 220);
  const Current = STEPS[step];

  const submit = async () => {
    setBusy(true);
    try {
      const body = Object.fromEntries(Object.entries(f).map(([k, v]) => [k, typeof v === "string" && v.trim() === "" ? null : v]));
      if (!isKeyService(f.service)) body.key_photos = [];
      await api.post("/leads", { ...body, kind: "quote", language: lang });
      setDone(true);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const onContinue = () => {
    if (!stepValid(step, f)) return toast.error(w.required);
    if (step === total - 1) return submit();
    return next();
  };

  return (
    <div data-testid="quote-wizard-page" className="min-h-screen bg-ink text-bone">
      <div className="bg-brand text-cream">
        <div className="mx-auto flex h-9 max-w-3xl items-center justify-end px-5"><LangSwitcher /></div>
      </div>
      <header className="mx-auto flex max-w-3xl items-center justify-between px-5 py-5">
        <Link to="/" data-testid="wizard-home-link"><Logo /></Link>
        <a href={PHONE_TEL} data-testid="wizard-call-link" className="inline-flex items-center gap-2 bg-brand px-4 py-2.5 text-cream"><Phone className="h-4 w-4" /><span className="font-display text-[12px] font-bold">{PHONE_DISPLAY}</span></a>
      </header>

      <main className="mx-auto max-w-3xl px-5 pb-24">
        {done ? (
          <motion.div data-testid="wizard-success" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="border border-white/15 bg-coal p-6 md:p-10">
            <span className="grid h-14 w-14 place-items-center bg-brand text-cream"><Check className="h-7 w-7" /></span>
            <h1 className="font-display mt-6 text-3xl font-black uppercase md:text-4xl">{w.doneT}</h1>
            <p className="mt-3 max-w-lg text-zinc-300">{w.doneB}</p>
            <div className="mt-8"><Summary f={f} w={w} /></div>
            <p className="mt-8 text-sm text-zinc-300">{w.urgentB}</p>
            <div className="mt-3 flex flex-col gap-3 sm:flex-row">
              <a href={PHONE_TEL} data-testid="wizard-success-call" className="inline-flex items-center justify-center gap-2 bg-brand px-6 py-3.5 font-display text-sm font-extrabold uppercase text-cream"><Phone className="h-4 w-4" /> {PHONE_DISPLAY}</a>
              <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noreferrer" data-testid="wizard-success-whatsapp" className="inline-flex items-center justify-center gap-2 bg-[#25D366] px-6 py-3.5 font-display text-sm font-extrabold uppercase" style={{ color: "#fff" }}>WhatsApp</a>
              <Link to="/" data-testid="wizard-success-home" className="inline-flex items-center justify-center border border-white/20 px-6 py-3.5 font-mono text-[12px] uppercase tracking-[0.16em] hover:border-brand">{w.home}</Link>
            </div>
          </motion.div>
        ) : (
          <>
            <div className="flex gap-1.5" data-testid="wizard-progress">
              {STEPS.map((_, i) => <span key={i} className={`h-1.5 flex-1 transition-colors duration-500 ${i <= step ? "bg-brand" : "bg-white/15"}`} />)}
            </div>
            <div data-testid="wizard-step-label" className="mt-4 font-mono text-[12px] uppercase tracking-[0.2em] text-brand">{w.step} {step + 1} {w.of} {total}</div>
            <motion.section key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }} className="mt-3 border border-white/15 bg-coal p-5 md:p-8">
              <h1 data-testid="wizard-step-title" className="font-display text-2xl font-black uppercase md:text-3xl">{w.s[step][0]}</h1>
              <p className="mt-2 text-sm text-zinc-300 md:text-base">{w.s[step][1]}</p>
              <div className="mt-7"><Current f={f} set={set} w={w} next={autoNext} /></div>
              {step === total - 1 && <div className="mt-8"><Summary f={f} w={w} /></div>}
              <div className="mt-8 flex items-center justify-between border-t border-white/15 pt-5">
                <button type="button" data-testid="wizard-back-button" disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))} className="inline-flex items-center gap-2 px-2 py-2 font-mono text-[12px] uppercase tracking-[0.16em] text-zinc-300 disabled:opacity-30"><ArrowLeft className="h-4 w-4" /> {w.back}</button>
                <button type="button" data-testid="wizard-next-button" disabled={busy} onClick={onContinue} className={`inline-flex items-center gap-2 px-6 py-3.5 font-display text-sm font-extrabold uppercase tracking-wider text-cream transition-[opacity,transform] duration-300 hover:-translate-y-0.5 ${stepValid(step, f) ? "bg-brand" : "bg-brand/50"}`}>
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                  {step === total - 1 ? (busy ? w.sending : w.submit) : w.next}
                  {step < total - 1 && <ArrowRight className="h-4 w-4" />}
                </button>
              </div>
            </motion.section>
            <p className="mt-5 flex items-center justify-center gap-2 text-center font-mono text-[11px] uppercase tracking-[0.14em] text-zinc-400"><Lock className="h-3.5 w-3.5" /> {w.secure}</p>
          </>
        )}
      </main>
    </div>
  );
}
